// React hook: composite My Activity region loader.
//
// Pairs with services/api-server/routes/my-activity-regions.mjs.
// Returns `{status, data, regions, degraded, error, lastUpdatedMs,
// refresh}` so each visual region can decide to render real data or
// a "No data yet" placeholder when the backing service is unavailable.
//
// Also wires the SSE stream: a stream frame triggers a (throttled)
// refresh so live regions tick without the rep hitting reload.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  MyActivityCompositeApi,
  MyActivityComposite,
  MyActivityCompositeStreamFrame,
  MyActivityRegionId,
  MyActivityScope,
  RegionEnvelope,
} from '../api/my-activity-composite.js';
import { RuntimeApiError } from '../api/runtimeClient.js';

export type MyActivityCompositeStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface UseMyActivityCompositeArgs {
  client: MyActivityCompositeApi | null;
  projectId: string | null;
  scope?: MyActivityScope;
  regions?: MyActivityRegionId[];
  // Throttle for refresh triggered by SSE frames (ms). Default 5000.
  streamRefreshThrottleMs?: number;
  // Fallback poll (ms). 0 disables. Default 60000.
  fallbackPollMs?: number;
  now?: () => number;
}

export interface UseMyActivityCompositeResult {
  status: MyActivityCompositeStatus;
  data: MyActivityComposite | null;
  regions: Partial<Record<MyActivityRegionId, RegionEnvelope<unknown>>>;
  degraded: Array<{ region: MyActivityRegionId; reason: string }>;
  error: RuntimeApiError | null;
  lastUpdatedMs: number | null;
  refresh: () => Promise<void>;
}

const DEFAULT_STREAM_THROTTLE_MS = 5_000;
const DEFAULT_FALLBACK_POLL_MS = 60_000;

export function useMyActivityComposite(
  args: UseMyActivityCompositeArgs,
): UseMyActivityCompositeResult {
  const {
    client,
    projectId,
    scope = 'today',
    regions,
    streamRefreshThrottleMs = DEFAULT_STREAM_THROTTLE_MS,
    fallbackPollMs = DEFAULT_FALLBACK_POLL_MS,
    now = Date.now,
  } = args;

  const [status, setStatus] = useState<MyActivityCompositeStatus>(
    client && projectId ? 'loading' : 'idle',
  );
  const [data, setData] = useState<MyActivityComposite | null>(null);
  const [error, setError] = useState<RuntimeApiError | null>(null);
  const [lastUpdatedMs, setLastUpdatedMs] = useState<number | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const lastRefreshedAtRef = useRef<number>(0);
  const pendingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef<boolean>(true);
  // Stabilize regions by stringifying so a caller passing a fresh array
  // literal doesn't trigger a reload on every render.
  const regionsKey = regions ? regions.slice().sort().join(',') : '';

  useEffect(() => () => { mountedRef.current = false; }, []);

  const refresh = useCallback(async (): Promise<void> => {
    if (!client || !projectId) return;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setStatus('loading');
    setError(null);
    try {
      const composite = await client.fetchComposite(projectId, {
        scope,
        ...(regions ? { regions } : {}),
        signal: controller.signal,
      });
      if (!mountedRef.current) return;
      setData(composite);
      setStatus('ready');
      setLastUpdatedMs(now());
      lastRefreshedAtRef.current = now();
    } catch (err) {
      if (!mountedRef.current) return;
      if (err instanceof RuntimeApiError && err.errorCode === 'ABORTED') return;
      const wrapped = err instanceof RuntimeApiError
        ? err
        : new RuntimeApiError({ kind: 'NETWORK_ERROR', status: 0, message: String(err), errorCode: 'NETWORK_ERROR' });
      setError(wrapped);
      setStatus('error');
    }
    // regionsKey stands in for `regions` so the hook does not re-run on
    // array identity. See regionsKey above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, projectId, scope, regionsKey, now]);

  useEffect(() => {
    if (!client || !projectId) {
      setStatus('idle');
      return;
    }
    void refresh();
    let unsubscribe: (() => void) | null = null;
    try {
      unsubscribe = client.subscribeStream(
        projectId,
        (_frame: MyActivityCompositeStreamFrame) => {
          const sinceLast = now() - lastRefreshedAtRef.current;
          if (sinceLast >= streamRefreshThrottleMs) {
            void refresh();
          } else if (!pendingTimerRef.current) {
            pendingTimerRef.current = setTimeout(() => {
              pendingTimerRef.current = null;
              void refresh();
            }, streamRefreshThrottleMs - sinceLast);
          }
        },
        (streamErr) => {
          // Non-fatal: initial fetch + manual refresh still work.
          if (!data) setError(streamErr);
        },
      );
    } catch (subscribeErr) {
      if (subscribeErr instanceof RuntimeApiError && !data) setError(subscribeErr);
    }

    const pollTimer = fallbackPollMs > 0 ? setInterval(() => void refresh(), fallbackPollMs) : null;

    return () => {
      if (unsubscribe) unsubscribe();
      if (pendingTimerRef.current) {
        clearTimeout(pendingTimerRef.current);
        pendingTimerRef.current = null;
      }
      if (pollTimer) clearInterval(pollTimer);
      if (abortRef.current) abortRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, projectId, refresh, streamRefreshThrottleMs, fallbackPollMs]);

  const regionsMap = (data?.regions ?? {}) as Partial<Record<MyActivityRegionId, RegionEnvelope<unknown>>>;
  const degraded = data?.degraded ?? [];

  return useMemo(
    () => ({ status, data, regions: regionsMap, degraded, error, lastUpdatedMs, refresh }),
    [status, data, regionsMap, degraded, error, lastUpdatedMs, refresh],
  );
}
